import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { DeckFullscreen } from '@/components/builder/DeckFullscreen';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { builderApi, isDeckContent } from '@/utils/api-pb';

/**
 * Presentation view at `/builder/:buildId/view`.
 *
 * Its own address, so a pitch can be opened straight from a bookmark with nothing of
 * the editor on screen — which is how this gets used: the deck is already finished and
 * the laptop is plugged into somebody's projector. Leaving the view returns to the
 * editor, since that is the only other place to go.
 */
export default function DeckViewPage() {
  const { buildId = '' } = useParams();
  const { t } = useTranslation('builder');
  const navigate = useLocalizedNavigate();

  const [html, setHtml] = useState('');
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [preview, data] = await Promise.all([
          builderApi.preview(buildId),
          builderApi.get(buildId),
        ]);
        if (cancelled) return;

        if (data.build.kind !== 'deck') {
          navigate(`/builder/${buildId}`);
          return;
        }

        setHtml(preview.html);
        setSlideCount(
          isDeckContent(data.content) ? data.content.slides.filter((slide) => slide.visible).length : 0,
        );
      } catch (error) {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : t('toast.error'));
        navigate('/builder');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [buildId, navigate, t]);

  if (!html) return <AccessibleLoader label={t('loading', { ns: 'common' })} />;

  return (
    <DeckFullscreen
      html={html}
      slideCount={slideCount}
      onExit={() => navigate(`/builder/${buildId}`)}
    />
  );
}
